/**
 * MyPostsFeed — Instagram-style personal feed shown at the top of the
 * user Dashboard. Lists every post the user has authored across all
 * content sections, with inline quick-edit, archive/restore, delete,
 * and a comments drawer for moderating replies.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Archive, Edit3, Trash2, RotateCcw, ExternalLink,
  MessageCircle, Eye, EyeOff, Heart, X, Save, ImageIcon,
  Share2, Copy, Mail,
} from "lucide-react";

const TYPE_LABELS = {
  causes: "Cause",
  appeals: "Appeal",
  help_offers: "Help offer",
  news_submissions: "News",
  memorial_nominations: "Memorial",
  family_trees: "Family tree",
  family_members: "Family member",
  family_events: "Family event",
  family_albums: "Family album",
  family_comments: "Family comment",
  sp_articles: "Article",
  events: "Event",
};

const TYPE_LINKS = {
  causes: (d) => `/causes/${d.slug || d.id}`,
  appeals: (d) => `/offer-help/${d.slug || d.id}`,
  help_offers: (d) => `/find-help/${d.slug || d.id}`,
  family_trees: (d) => `/family/${d.slug || d.id}`,
  sp_articles: (d) => `/articles/${d.slug}`,
  events: (d) => `/events/${d.slug || d.id}`,
};

const CHIPS = [
  { v: "all", label: "All" },
  { v: "active", label: "Active" },
  { v: "archived", label: "Archived" },
];

export default function MyPostsFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/my-content");
      setItems(data.items || []);
    } catch (e) { toast.error("Failed to load your posts"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const types = Array.from(new Set(items.map((i) => i.content_type)));

  const filtered = items.filter((it) => {
    if (statusFilter === "active" && it.status === "archived") return false;
    if (statusFilter === "archived" && it.status !== "archived") return false;
    if (typeFilter !== "all" && it.content_type !== typeFilter) return false;
    return true;
  });

  return (
    <section data-testid="my-posts-feed" className="wofy-card mt-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">MY POSTS</div>
          <h2 className="font-display text-2xl mt-1">Everything you've shared</h2>
        </div>
        <Link to="/submit-cause" className="wofy-btn-ghost text-xs" data-testid="feed-new-cause">+ New post</Link>
      </div>

      {/* Status filter */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        {CHIPS.map((c) => (
          <button
            key={c.v}
            onClick={() => setStatusFilter(c.v)}
            data-testid={`feed-status-${c.v}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${statusFilter === c.v ? "bg-[var(--wofy-primary)] text-white border-transparent" : "hover:bg-[var(--wofy-surface)]"}`}
            style={statusFilter !== c.v ? { borderColor: "var(--wofy-border)" } : undefined}
          >
            {c.label}
          </button>
        ))}
        {types.length > 1 && (
          <>
            <span className="mx-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>·</span>
            <button
              onClick={() => setTypeFilter("all")}
              data-testid="feed-type-all"
              className={`text-xs px-3 py-1.5 rounded-full border transition ${typeFilter === "all" ? "bg-[var(--wofy-ink)] text-white border-transparent" : "hover:bg-[var(--wofy-surface)]"}`}
              style={typeFilter !== "all" ? { borderColor: "var(--wofy-border)" } : undefined}
            >
              All types
            </button>
            {types.map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                data-testid={`feed-type-${t}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${typeFilter === t ? "bg-[var(--wofy-ink)] text-white border-transparent" : "hover:bg-[var(--wofy-surface)]"}`}
                style={typeFilter !== t ? { borderColor: "var(--wofy-border)" } : undefined}
              >
                {TYPE_LABELS[t] || t}
              </button>
            ))}
          </>
        )}
      </div>

      {loading ? (
        <p className="mt-6 text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading your feed…</p>
      ) : filtered.length === 0 ? (
        <div className="mt-6 py-12 text-center text-sm rounded-xl border-2 border-dashed" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
          {items.length === 0
            ? <>You haven't shared anything yet. <Link to="/submit-cause" className="underline">Post your first story →</Link></>
            : <>Nothing here yet with these filters.</>}
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="feed-grid">
          {filtered.map((it) => (
            <PostCard key={`${it.content_type}-${it.id}`} item={it} onChanged={load} />
          ))}
        </div>
      )}
    </section>
  );
}

function PostCard({ item, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [title, setTitle] = useState(item.title || "");
  const [excerpt, setExcerpt] = useState(item.excerpt || "");
  const [busy, setBusy] = useState(false);

  const linkFn = TYPE_LINKS[item.content_type];
  const url = linkFn ? linkFn(item) : null;
  const archived = item.status === "archived";
  const label = TYPE_LABELS[item.content_type] || item.content_type;

  const doAction = async (action) => {
    let note = null;
    if (action === "delete") {
      if (!window.confirm("Delete this post permanently? This cannot be undone.")) return;
    }
    setBusy(true);
    try {
      await http.post(`/my-content/${item.content_type}/${item.id}`, { action, note });
      toast.success(`Post ${action}d`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const patch = {};
      if (title !== item.title) patch.title = title;
      if (excerpt !== item.excerpt) {
        // Write into the most common description field. Backend stores whatever we send.
        patch.description = excerpt;
        patch.excerpt = excerpt;
      }
      if (Object.keys(patch).length === 0) {
        setEditing(false);
        return;
      }
      await http.post(`/my-content/${item.content_type}/${item.id}`, { action: "edit", patch });
      toast.success("Saved");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <article
      data-testid={`post-card-${item.id}`}
      className="rounded-xl border overflow-hidden bg-white flex flex-col transition hover:shadow-md"
      style={{ borderColor: "var(--wofy-border)", opacity: archived ? 0.6 : 1 }}
    >
      {/* Cover */}
      <div className="relative aspect-[4/3] bg-[var(--wofy-surface)] overflow-hidden">
        {item.image ? (
          <img src={item.image} alt={item.title || ""} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-10 h-10 opacity-30" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1.5">
          <span className="text-[10px] uppercase font-mono tracking-wider bg-white/90 backdrop-blur px-2 py-1 rounded-full">
            {label}
          </span>
          {archived && (
            <span className="text-[10px] uppercase font-mono tracking-wider bg-[#B23A3A] text-white px-2 py-1 rounded-full">
              Archived
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex-1 flex flex-col">
        {editing ? (
          <div className="space-y-2">
            <input
              data-testid={`post-edit-title-${item.id}`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-lg border px-2.5 py-1.5 text-sm font-semibold"
              style={{ borderColor: "var(--wofy-border)" }}
              placeholder="Title"
            />
            <textarea
              data-testid={`post-edit-body-${item.id}`}
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              rows={3}
              className="w-full rounded-lg border px-2.5 py-1.5 text-xs"
              style={{ borderColor: "var(--wofy-border)" }}
              placeholder="Short description"
            />
            <div className="flex gap-2">
              <button
                onClick={saveEdit}
                disabled={busy}
                data-testid={`post-save-${item.id}`}
                className="wofy-btn-primary text-xs flex-1 justify-center disabled:opacity-60"
              >
                <Save className="w-3 h-3" /> Save
              </button>
              <button
                onClick={() => { setEditing(false); setTitle(item.title); setExcerpt(item.excerpt); }}
                className="wofy-btn-ghost text-xs"
                data-testid={`post-cancel-${item.id}`}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
              {item.created_at ? new Date(item.created_at).toLocaleDateString(undefined, { dateStyle: "medium" }) : ""}
            </div>
            <h3 className="font-semibold mt-0.5 line-clamp-2">{item.title || "(untitled)"}</h3>
            {item.excerpt && (
              <p className="text-xs mt-1 line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>
                {item.excerpt}
              </p>
            )}
          </>
        )}

        {/* Stats */}
        <div className="flex items-center gap-3 mt-3 text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
          <span className="flex items-center gap-1" title="Views"><Eye className="w-3 h-3" /> {item.view_count || 0}</span>
          <span className="flex items-center gap-1" title="Likes"><Heart className="w-3 h-3" /> {item.like_count || 0}</span>
          <button
            onClick={() => setShowComments((s) => !s)}
            data-testid={`post-toggle-comments-${item.id}`}
            className="flex items-center gap-1 hover:opacity-70"
            title="Comments"
          >
            <MessageCircle className="w-3 h-3" /> {item.comment_count || 0}
          </button>
          <span className="flex items-center gap-1" title="Shares" data-testid={`post-shares-${item.id}`}>
            <Share2 className="w-3 h-3" /> {item.share_count || 0}
          </span>
        </div>

        {/* Actions */}
        <div className="mt-3 pt-3 border-t flex items-center gap-1 flex-wrap relative" style={{ borderColor: "var(--wofy-border)" }}>
          {url && (
            <Link to={url} className="wofy-btn-ghost text-xs" data-testid={`post-view-${item.id}`} title="View">
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}
          <button
            onClick={() => setEditing((s) => !s)}
            disabled={editing || busy}
            className="wofy-btn-ghost text-xs disabled:opacity-40"
            data-testid={`post-edit-${item.id}`}
            title="Quick edit"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          {url && (
            <button
              onClick={() => setShowShare((s) => !s)}
              className="wofy-btn-ghost text-xs"
              data-testid={`post-share-${item.id}`}
              title="Share"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={async () => {
              const next = item.public_visible === false;
              setBusy(true);
              try {
                await http.post(`/my-content/${item.content_type}/${item.id}/visibility`, { visible: next });
                toast.success(next ? "Now visible on your profile" : "Hidden from your profile");
                onChanged?.();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not update visibility");
              } finally { setBusy(false); }
            }}
            disabled={busy}
            className="wofy-btn-ghost text-xs disabled:opacity-40"
            data-testid={`post-visibility-${item.id}`}
            title={item.public_visible === false ? "Hidden from profile — click to show" : "Visible on profile — click to hide"}
          >
            {item.public_visible === false
              ? <EyeOff className="w-3.5 h-3.5" style={{ color: "#B23A3A" }} />
              : <Eye className="w-3.5 h-3.5" />}
          </button>
          {archived ? (
            <button
              onClick={() => doAction("restore")}
              disabled={busy}
              className="wofy-btn-ghost text-xs"
              data-testid={`post-restore-${item.id}`}
              title="Restore"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={() => doAction("archive")}
              disabled={busy}
              className="wofy-btn-ghost text-xs"
              data-testid={`post-archive-${item.id}`}
              title="Archive"
            >
              <Archive className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => doAction("delete")}
            disabled={busy}
            className="wofy-btn-ghost text-xs ml-auto"
            data-testid={`post-delete-${item.id}`}
            title="Delete"
            style={{ color: "#B23A3A" }}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {showShare && url && (
            <SharePopover
              item={item}
              publicUrl={`${window.location.origin}${url}`}
              shareUrl={`${window.location.origin}/api/s/${item.content_type}/${item.slug || item.id}`}
              onClose={() => setShowShare(false)}
            />
          )}
        </div>

        {showComments && (
          <CommentsDrawer
            contentType={item.content_type}
            contentId={item.id}
            onClose={() => setShowComments(false)}
            onDeleted={onChanged}
          />
        )}
      </div>
    </article>
  );
}

function CommentsDrawer({ contentType, contentId, onClose, onDeleted }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let alive = true;
    http.get(`/my-content/${contentType}/${contentId}/comments`)
      .then((r) => { if (alive) setItems(r.data.items || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [contentType, contentId]);

  const del = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    setBusyId(cid);
    try {
      await http.delete(`/my-content/${contentType}/${contentId}/comments/${cid}`);
      setItems((cur) => (cur || []).filter((c) => c.id !== cid));
      toast.success("Comment removed");
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete comment");
    } finally { setBusyId(null); }
  };

  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--wofy-border)" }} data-testid={`comments-drawer-${contentId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-[10px]">Comments</div>
        <button onClick={onClose} className="wofy-btn-ghost text-xs" data-testid={`comments-close-${contentId}`}>
          <X className="w-3 h-3" />
        </button>
      </div>
      {items === null ? (
        <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>No comments yet.</p>
      ) : (
        <ul className="space-y-2 max-h-56 overflow-auto pr-1">
          {items.map((c) => (
            <li key={c.id} className="text-xs flex items-start gap-2 border-l-2 pl-2" style={{ borderColor: "var(--wofy-border)" }}>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{c.author_name || c.name || "Someone"}</div>
                <div className="text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
                  {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
                </div>
                <p className="mt-1 break-words">{c.body || c.text || c.content || ""}</p>
              </div>
              <button
                onClick={() => del(c.id)}
                disabled={busyId === c.id}
                data-testid={`comment-delete-${c.id}`}
                className="wofy-btn-ghost text-[10px] disabled:opacity-40"
                style={{ color: "#B23A3A" }}
                title="Delete comment"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SharePopover({ item, publicUrl, shareUrl, onClose }) {
  // publicUrl → what "Copy link" gives the user (clean, human-friendly).
  // shareUrl  → what social scrapers hit for rich OG previews. Redirects
  // real users to publicUrl automatically.
  const shareText = item.title
    ? `${item.title} — on Social Pulse`
    : "Check this out on Social Pulse";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
    onClose?.();
  };

  const open = (href) => {
    window.open(href, "_blank", "noopener,noreferrer");
    onClose?.();
  };

  const linkForScrapers = shareUrl || publicUrl;
  const encoded = encodeURIComponent(`${shareText} ${linkForScrapers}`);
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(linkForScrapers);

  const options = [
    { label: "Copy link", icon: Copy, onClick: copy, testid: "share-copy" },
    { label: "WhatsApp", icon: MessageCircle, onClick: () => open(`https://wa.me/?text=${encoded}`), testid: "share-whatsapp" },
    { label: "X / Twitter", icon: Share2, onClick: () => open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`), testid: "share-twitter" },
    { label: "Facebook", icon: Share2, onClick: () => open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`), testid: "share-facebook" },
    { label: "Email", icon: Mail, onClick: () => open(`mailto:?subject=${encodedText}&body=${encoded}`), testid: "share-email" },
  ];

  return (
    <div
      data-testid={`share-popover-${item.id}`}
      className="absolute right-2 bottom-full mb-2 z-20 w-52 rounded-xl border shadow-lg bg-white overflow-hidden"
      style={{ borderColor: "var(--wofy-border)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="overline text-[10px]">Share this post</div>
        <button onClick={onClose} className="wofy-btn-ghost text-xs" data-testid={`share-close-${item.id}`}>
          <X className="w-3 h-3" />
        </button>
      </div>
      <ul className="py-1">
        {options.map((o) => {
          const Icon = o.icon;
          return (
            <li key={o.label}>
              <button
                onClick={o.onClick}
                data-testid={`${o.testid}-${item.id}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--wofy-surface)] transition text-left"
              >
                <Icon className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
                {o.label}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
